import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, ArrowUpFromLine, CheckCircle, Inbox, Layers, Loader, MapPin } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { NumericInput } from '../../../../common/inputs';
import { consolidationService } from '../../../../../services/consolidationService';
import { FreeUpCandidate } from '../../../../../types/consolidation';
import { ConfirmLine } from '../types';
import { OpenCapacityArgs } from '../useCapacityUpdate';
import { clampQty, parsedQty } from '../utils';

interface EmptyBinViewProps {
    binCode: string;
    warehouseID: string;
    /** Bumpes av ConsolidationApp etter utført flytt → re-laster forslagene. */
    refreshToken: number;
    onConfirm: (lines: ConfirmLine[]) => void;
    /** Bytt til «Trekk inn hit» for samme bin (motsatt retning). */
    onSwitchToPullIn: () => void;
    /** Åpne «Oppdater kapasitet» for foreslått destinasjon + vare. */
    onUpdateCapacity?: (args: OpenCapacityArgs) => void;
}

interface RowState {
    checked: boolean;
    qty: string;
}

/**
 * Visning «Frigjør denne lokasjonen» (kilde-først for ÉN skannet bin).
 * Operatøren har skannet en nesten-tom lokasjon og vil flytte innholdet ut for
 * å frigjøre den. Viser hver vare i binnen med foreslått destinasjon (samme vare,
 * samme bintype, mest headroom). Varer uten sikker destinasjon kan fortsatt velges
 * og få destinasjon satt manuelt i bekreftelsesarket.
 */
export const EmptyBinView: React.FC<EmptyBinViewProps> = ({
    binCode,
    warehouseID,
    refreshToken,
    onConfirm,
    onSwitchToPullIn,
    onUpdateCapacity
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<FreeUpCandidate[]>([]);
    const [rows, setRows] = useState<Record<string, RowState>>({});

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await consolidationService.getBinFreeCandidates(binCode, warehouseID);
                if (cancelled) return;
                setCandidates(result);
                // Forhåndshuk varer som har en foreslått destinasjon; varer uten lar vi
                // stå avhuket til operatøren bevisst velger en destinasjon.
                const init: Record<string, RowState> = {};
                result.forEach((c) => {
                    init[c.itemNo] = {
                        checked: !!c.suggestedToBinCode,
                        qty: String(c.quantity)
                    };
                });
                setRows(init);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                setCandidates([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [binCode, warehouseID, refreshToken]);

    const selectedCount = useMemo(
        () => candidates.filter((c) => rows[c.itemNo]?.checked && parsedQty(rows[c.itemNo]?.qty ?? '0') > 0).length,
        [candidates, rows]
    );

    // Binnen frigjøres bare hvis den er én-vare (da tømmer ett flytt den helt).
    // For fler-vare-bins er det konservativt false — frigjøringen verifiseres uansett
    // når ordren utføres i Plassering.
    const singleItemBin = candidates.length === 1;

    const setRow = (itemNo: string, patch: Partial<RowState>) =>
        setRows((prev) => ({ ...prev, [itemNo]: { ...prev[itemNo], ...patch } }));

    const handleConfirm = () => {
        const lines: ConfirmLine[] = candidates
            .filter((c) => rows[c.itemNo]?.checked && parsedQty(rows[c.itemNo]?.qty ?? '0') > 0)
            .map((c) => ({
                id: `${c.itemNo}|${binCode}|${c.suggestedToBinCode || ''}`,
                fromBinCode: binCode,
                toBinCode: c.suggestedToBinCode || '',
                originalToBinCode: c.suggestedToBinCode || '',
                itemNo: c.itemNo,
                description1: c.description1,
                quantity: rows[c.itemNo].qty,
                maxQuantity: c.quantity,
                freesBin: singleItemBin,
                capacityConfidence: c.capacityConfidence,
                status: 'pending' as const
            }));
        if (lines.length > 0) onConfirm(lines);
    };

    return (
        <div className="space-y-3">
            {/* Bin-kort + bytt-retning */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-cyan-200">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <ArrowUpFromLine className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                                {t('warehouse.modules.consolidation.emptyBin.title', {}, 'Frigjør denne lokasjonen')}
                            </span>
                        </div>
                        <div className="font-mono text-xl font-semibold text-gray-900 break-all">{binCode}</div>
                    </div>
                    <button
                        onClick={onSwitchToPullIn}
                        className="flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 min-h-[44px]"
                    >
                        {t('warehouse.modules.consolidation.emptyBin.switchToPullIn', {}, 'Trekk inn hit i stedet →')}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 bg-white rounded-lg border shadow-sm">
                    <Loader className="animate-spin h-6 w-6 text-cyan-500" />
                    <span className="ml-2 text-sm text-gray-600">
                        {t('warehouse.modules.consolidation.emptyBin.loading', {}, 'Henter innhold…')}
                    </span>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start text-sm text-red-800">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    <span className="break-words">{error}</span>
                </div>
            ) : candidates.length === 0 ? (
                <div className="bg-white rounded-lg p-6 shadow-sm border text-center">
                    <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                        {t('warehouse.modules.consolidation.emptyBin.empty', {}, 'Lokasjonen er tom eller har ingen flyttbar saldo')}
                    </p>
                </div>
            ) : (
                <>
                    {candidates.map((c) => {
                        const row = rows[c.itemNo] ?? { checked: false, qty: '0' };
                        const hasDest = !!c.suggestedToBinCode;
                        return (
                            <div key={c.itemNo} className="bg-white rounded-lg p-3 shadow-sm border">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={row.checked}
                                        onChange={(e) => setRow(c.itemNo, { checked: e.target.checked })}
                                        className="mt-1 h-5 w-5 flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                                            <span className="font-mono text-sm font-semibold text-gray-900 break-all">
                                                {c.itemNo}
                                            </span>
                                        </div>
                                        {c.description1 && (
                                            <div className="text-xs text-gray-500 break-words mt-0.5">{c.description1}</div>
                                        )}
                                        <div className="flex items-center gap-1 mt-1 text-sm">
                                            {hasDest ? (
                                                <>
                                                    <ArrowRight className="h-4 w-4 text-gray-400" />
                                                    <span className="font-mono text-gray-800">{c.suggestedToBinCode}</span>
                                                    {c.destinationQuantity !== undefined && c.destinationQuantity !== null && (
                                                        <span className="text-xs text-gray-500 whitespace-nowrap">
                                                            — {t('warehouse.modules.consolidation.freeUp.existingQty', { '0': c.destinationQuantity }, `${c.destinationQuantity} fra før`)}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-xs text-gray-500">
                                                    {t('warehouse.modules.consolidation.freeUp.noDestination', {}, 'Ingen forslag — velg destinasjon manuelt')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1">
                                            {c.guaranteedFit ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                                    <CheckCircle className="h-3 w-3" />
                                                    {t('warehouse.modules.consolidation.freeUp.guaranteed', {}, '1 flytt = 1 frigjort')}
                                                </span>
                                            ) : hasDest ? (
                                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                                    {c.capacityConfidence === 'estimated'
                                                        ? t('warehouse.modules.consolidation.freeUp.approxCapacity', {}, 'ca. kapasitet')
                                                        : t('warehouse.modules.consolidation.freeUp.confirmManually', {}, 'bekreft manuelt')}
                                                </span>
                                            ) : null}
                                        </div>
                                        {onUpdateCapacity && hasDest && !c.guaranteedFit && (
                                            <button
                                                onClick={() => onUpdateCapacity({
                                                    binCode: c.suggestedToBinCode!,
                                                    itemNo: c.itemNo,
                                                    itemDescription: c.description1,
                                                    currentQuantity: c.destinationQuantity ?? undefined
                                                })}
                                                className="mt-1 flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-cyan-700 border border-cyan-200 hover:bg-cyan-50 min-h-[36px]"
                                                title={t('warehouse.modules.consolidation.capacity.updateHint', {}, 'Det er plass? Oppdater kapasiteten')}
                                            >
                                                <Layers className="h-3.5 w-3.5" />
                                                {t('warehouse.modules.consolidation.capacity.update', {}, 'Oppdater kapasitet')}
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex-shrink-0">
                                        <NumericInput
                                            mode="decimal"
                                            value={row.qty}
                                            onChange={(e) => setRow(c.itemNo, { qty: clampQty(e.target.value, c.quantity) })}
                                            disabled={!row.checked}
                                            className="w-20 p-2 border border-gray-300 rounded-lg text-right text-base focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-h-[44px] disabled:bg-gray-100"
                                        />
                                        <div className="text-xs text-gray-400 text-right mt-0.5">/ {c.quantity}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <button
                        onClick={handleConfirm}
                        disabled={selectedCount === 0}
                        className={`w-full py-4 rounded-lg font-semibold text-base min-h-[44px] transition-colors ${
                            selectedCount > 0
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {t('warehouse.modules.consolidation.emptyBin.confirm', { '0': selectedCount }, `Opprett utleggsordre (${selectedCount} linjer)`)}
                    </button>
                </>
            )}
        </div>
    );
};
